import { AxiosResponse } from 'axios';
import { addMinutes } from 'date-fns';
import {
  all,
  call,
  put,
  PutEffect,
  select,
  takeLeading,
  fork,
  takeLatest,
} from 'redux-saga/effects';
import { Dashboards } from '../../components';
import {
  getCountriesForPermissions,
  hasPermissions,
  findDashboardConciliationsStateResponseMap,
  findConciliationsStatusRequestMap,
  handleConciliationsStateViewMode,
  findMastersStatusResponseMap,
  findSovosStatusResponseMap,
  findSovosImportRequestMap,
  findDashboardTotalsByCountryRequestMap,
  findDashboardTotalsByCountryResponseMap,
  findClosureEvolutionRequestMap,
} from '../../helpers';
import {
  AppStorage,
  ConciliationApi,
  FindMastersStatusResponse,
  FindSovosStatusResponse,
} from '../../services';
import {
  AppParameters,
  AppPermissions,
  HttpRequestError,
  Permission,
  FindConciliationsStatusResponse,
  ConciliationsStateTimeLapse,
  Country,
  ClosureEvolutionRow,
} from '../../types';
import {
  activeCountriesSelector,
  dashboardClosureEvolutionFilterEndDateSelector,
  dashboardClosureEvolutionFilterStartDateSelector,
  dashboardCountrySelectedSelector,
  filterParametersDataSelector,
  userPermissionsDataSelector,
} from '../selectors';
import {
  findDashboardConciliationsStateSuccess,
  findDashboardConciliationsFailure,
  findMastersStatus2Failure,
  findMastersStatus2Success,
  findSovosImportStatusesFailure,
  findSovosImportStatusesSuccess,
  getDashboardConciliationsStateYesterdaySuccess,
  getDashboardConciliationsStateYesterdayFailure,
  getDashboardConciliationsStateCurrentMonthSuccess,
  getDashboardConciliationsStateCurrentMonthFailure,
  getDashboardConciliationsStateLastMonthSuccess,
  getDashboardConciliationsStateLastMonthFailure,
  getDashboardTotalsYesterdaySuccess,
  getDashboardTotalsYesterdayFailure,
  getDashboardTotalsCurrentMonthSuccess,
  getDashboardTotalsCurrentMonthFailure,
  getDashboardTotalsLastMonthSuccess,
  getDashboardTotalsLastMonthFailure,
  getDashboardClosureEvolutionSuccess,
  getDashboardClosureEvolutionFailure,
} from './actionCreators';
import {
  ActionType,
  ChangeDashboardCountrySelectedAction,
  FindDashboardConciliationsStateCardsRequestedAction,
  State,
} from './types';

function* findAll() {
  const permissions: Permission[] = yield select(userPermissionsDataSelector);
  const requestsMap: { [key: string]: PutEffect } = {};

  AppStorage.removeDashboardData();

  Dashboards.forEach(dashboard => {
    if (
      hasPermissions(
        permissions.map(i => i.permission),
        dashboard.permissions
      )
    ) {
      requestsMap[dashboard.name] = dashboard.request as PutEffect;
    }
  });

  const requestsCall = Object.keys(requestsMap).map(key => requestsMap[key]);

  yield all(requestsCall);
}

function* findDashboardConciliationsState() {
  try {
    const timeLapse: ConciliationsStateTimeLapse = ConciliationsStateTimeLapse.yesteday;

    const storagedData = AppStorage.getDashboardConciliationsStateData('multiCountry', timeLapse);
    if (!storagedData) {
      const appParameters: AppParameters | undefined = yield select(filterParametersDataSelector);
      const { from, to } = handleConciliationsStateViewMode(timeLapse);

      const permissions: Permission[] = yield select(userPermissionsDataSelector);
      const { data: countries } = yield select(activeCountriesSelector);
      const authorizedCountries = getCountriesForPermissions(
        [AppPermissions.CONCILIATIONS_DASHBOARD],
        permissions,
        countries
      ).map(i => i?.countryCode);
      if (!authorizedCountries) return;
      const { data }: AxiosResponse<FindConciliationsStatusResponse[]> = yield call(
        ConciliationApi.findDashboardConciliationsState,
        findConciliationsStatusRequestMap(from, to, authorizedCountries)
      );
      yield put(
        findDashboardConciliationsStateSuccess(findDashboardConciliationsStateResponseMap(data))
      );
      AppStorage.setDashboardConciliationsStateData(
        'multiCountry',
        timeLapse,
        findDashboardConciliationsStateResponseMap(data),
        addMinutes(new Date(), appParameters?.dashboardTimer || 10)
      );
    } else {
      yield put(findDashboardConciliationsStateSuccess(storagedData));
    }
  } catch (error) {
    yield put(findDashboardConciliationsFailure(error as HttpRequestError));
  }
}

function* findSovosStatus() {
  try {
    const permissions: Permission[] = yield select(userPermissionsDataSelector);
    const { data: countries } = yield select(activeCountriesSelector);
    const authorizedCountries = getCountriesForPermissions(
      [AppPermissions.DASHBOARD_FISCAL_IMPORT],
      permissions,
      countries
    ).map(i => i?.countryCode);
    if (!authorizedCountries) return;

    const { data }: AxiosResponse<FindSovosStatusResponse> = yield call(
      ConciliationApi.findSovosStatus,
      findSovosImportRequestMap(authorizedCountries)
    );
    yield put(findSovosImportStatusesSuccess(findSovosStatusResponseMap(data)));
  } catch (error) {
    yield put(findSovosImportStatusesFailure(error as HttpRequestError));
  }
}

function* findMastersStatus2() {
  try {
    const { data }: AxiosResponse<FindMastersStatusResponse> = yield call(
      ConciliationApi.findMastersStatus2
    );
    yield put(findMastersStatus2Success(findMastersStatusResponseMap(data)));
  } catch (error) {
    yield put(findMastersStatus2Failure(error as HttpRequestError));
  }
}

function* getDashboardConciliationsCardState(
  countryCode: string,
  timeLapse: ConciliationsStateTimeLapse,
  success: (payload: any) => any,
  failure: (error: HttpRequestError) => any
) {
  try {
    const storagedData = AppStorage.getDashboardConciliationsStateData(
      'card',
      timeLapse,
      countryCode
    );

    if (!storagedData) {
      const appParameters: AppParameters | undefined = yield select(filterParametersDataSelector);
      const { from, to } = handleConciliationsStateViewMode(timeLapse);

      const permissions: Permission[] = yield select(userPermissionsDataSelector);
      const { data: countries } = yield select(activeCountriesSelector);
      const countrySelected: Country = countries.find(
        (country: Country) => country.countryCode === countryCode
      );
      const authorizedCountries = getCountriesForPermissions(
        [AppPermissions.CONCILIATIONS_DASHBOARD],
        permissions,
        [countrySelected]
      ).map(i => i?.countryCode);
      if (!authorizedCountries) return;
      const { data }: AxiosResponse<FindConciliationsStatusResponse[]> = yield call(
        ConciliationApi.findDashboardConciliationsState,
        findConciliationsStatusRequestMap(from, to, authorizedCountries)
      );
      // const mappedResponse =
      yield put(success(findDashboardConciliationsStateResponseMap(data)));
      AppStorage.setDashboardConciliationsStateData(
        'card',
        timeLapse,
        findDashboardConciliationsStateResponseMap(data),
        addMinutes(new Date(), appParameters?.dashboardTimer || 10),
        countryCode
      );
    } else {
      yield put(success(storagedData));
    }
  } catch (error) {
    yield put(failure(error as HttpRequestError));
  }
}

function* getDashboardConciliationsStateCards(
  action: ChangeDashboardCountrySelectedAction | FindDashboardConciliationsStateCardsRequestedAction
) {
  const countrySelected: string = yield select(dashboardCountrySelectedSelector);

  const countryCode = action.countryCode || countrySelected;
  if (!countryCode) {
    return;
  }
  yield all([
    fork(
      getDashboardConciliationsCardState,
      countryCode,
      ConciliationsStateTimeLapse.yesteday,
      getDashboardConciliationsStateYesterdaySuccess,
      getDashboardConciliationsStateYesterdayFailure
    ),
    fork(
      getDashboardConciliationsCardState,
      countryCode,
      ConciliationsStateTimeLapse.currentMonth,
      getDashboardConciliationsStateCurrentMonthSuccess,
      getDashboardConciliationsStateCurrentMonthFailure
    ),
    fork(
      getDashboardConciliationsCardState,
      countryCode,
      ConciliationsStateTimeLapse.lastMonth,
      getDashboardConciliationsStateLastMonthSuccess,
      getDashboardConciliationsStateLastMonthFailure
    ),
  ]);
}

function* getDashboardTotalsCards() {
  try {
    const countrySelected: string | null = yield select(dashboardCountrySelectedSelector);
    if (!countrySelected) return;

    const permissions: Permission[] = yield select(userPermissionsDataSelector);
    const { data: countries } = yield select(activeCountriesSelector);

    const countryObj: Country = countries.find((c: Country) => c.countryCode === countrySelected);

    const authorizedCountries = getCountriesForPermissions(
      [AppPermissions.CONCILIATIONS_DASHBOARD],
      permissions,
      [countryObj]
    ).map(i => i?.countryCode);

    if (!authorizedCountries?.length) return;

    const run = function* (
      timeLapse: ConciliationsStateTimeLapse,
      success: (payload: any) => any,
      failure: (error: HttpRequestError) => any
    ) {
      try {
        const { from, to } = handleConciliationsStateViewMode(timeLapse);

        const { data }: AxiosResponse<any> = yield call(
          ConciliationApi.findDashboardTotalsByCountry,
          findDashboardTotalsByCountryRequestMap(from, to, authorizedCountries)
        );

        yield put(success(findDashboardTotalsByCountryResponseMap(data)));
      } catch (e) {
        yield put(failure(e as HttpRequestError));
      }
    };

    yield all([
      fork(
        run,
        ConciliationsStateTimeLapse.yesteday,
        getDashboardTotalsYesterdaySuccess,
        getDashboardTotalsYesterdayFailure
      ),
      fork(
        run,
        ConciliationsStateTimeLapse.currentMonth,
        getDashboardTotalsCurrentMonthSuccess,
        getDashboardTotalsCurrentMonthFailure
      ),
      fork(
        run,
        ConciliationsStateTimeLapse.lastMonth,
        getDashboardTotalsLastMonthSuccess,
        getDashboardTotalsLastMonthFailure
      ),
    ]);
  } catch (e) {
    const err = e as HttpRequestError;
    yield put(getDashboardTotalsYesterdayFailure(err));
    yield put(getDashboardTotalsCurrentMonthFailure(err));
    yield put(getDashboardTotalsLastMonthFailure(err));
  }
}

function* findDashboardClosureEvolution() {
  try {
    const countrySelected: string | null = yield select(dashboardCountrySelectedSelector);

    if (!countrySelected) {
      return;
    }
    const startDate: Date | null = yield select(dashboardClosureEvolutionFilterStartDateSelector);
    const endDate: Date | null = yield select(dashboardClosureEvolutionFilterEndDateSelector);

    const now = new Date();
    const from = startDate ?? new Date(now.getFullYear(), 0, 1);
    const to = endDate ?? now;

    const request = findClosureEvolutionRequestMap(from, to, [countrySelected]);

    const { data } = yield call(ConciliationApi.findDashboardClosureEvolution, request);

    yield put(getDashboardClosureEvolutionSuccess(data));
  } catch (error) {
    yield put(getDashboardClosureEvolutionFailure(error as HttpRequestError));
  }
}

export default [
  takeLeading(ActionType.findAllRequest, findAll),
  takeLeading(
    [ActionType.findDashboardConciliationsStateRequested],
    findDashboardConciliationsState
  ),
  takeLeading(ActionType.findDashboardClosureEvolutionRequested, findDashboardClosureEvolution),
  takeLatest(
    [
      ActionType.changeDashboardCountrySelected,
      ActionType.findDashboardConciliationsStateCardsRequested,
    ],
    getDashboardConciliationsStateCards
  ),
  takeLatest(
    [
      ActionType.findDashboardTotalsCardsRequested,
      ActionType.changeDashboardCountrySelected,
      ActionType.setSingleCountryDashboard,
    ],
    getDashboardTotalsCards
  ),
  takeLatest(
    [ActionType.changeDashboardCountrySelected, ActionType.setSingleCountryDashboard],
    findDashboardClosureEvolution
  ),
  takeLeading(ActionType.findMastersStatus2Requested, findMastersStatus2),
  takeLeading(ActionType.findSovosImportStatusesRequested, findSovosStatus),
];
